import ScrollReveal from "@/components/ScrollReveal";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

function SVSGauge() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  // SVS score of 97.4 out of 100
  const score = 97.4;
  const circumference = 2 * Math.PI * 54; // radius 54
  const strokeDashoffset = circumference - (score / 100) * circumference * 0.75; // 270deg arc

  return (
    <div ref={ref} className="flex items-center justify-center">
      <div className="relative w-48 h-48 md:w-56 md:h-56">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-[135deg]">
          {/* Background arc */}
          <circle
            cx="60" cy="60" r="54"
            fill="none"
            stroke="#1E293B"
            strokeWidth="3"
            strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
            strokeLinecap="round"
          />
          {/* Animated score arc */}
          <motion.circle
            cx="60" cy="60" r="54"
            fill="none"
            stroke="#D4AF37"
            strokeWidth="3"
            strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
            strokeLinecap="round"
            initial={{ strokeDashoffset: circumference * 0.75 }}
            animate={isInView ? { strokeDashoffset } : { strokeDashoffset: circumference * 0.75 }}
            transition={{ duration: 2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            className="font-serif text-4xl md:text-5xl font-bold text-white"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            97.4
          </motion.span>
          <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">SVS Rating</span>
        </div>
      </div>
    </div>
  );
}

const metrics = [
  { value: "$2.8B", label: "Institutional Assets Under Analysis" },
  { value: "140+", label: "Regulatory Frameworks Mapped" },
  { value: "23", label: "Regulatory Bodies Accepting SVS" },
  { value: "15s", label: "Real-Time Score Refresh" },
];

const bulletPoints = [
  "Accepted by 23 regulatory bodies across 14 jurisdictions",
  "Full audit trail for compliance and board reporting",
  "Designed for Basel III/IV digital asset classification",
  "Post-quantum cryptographic vulnerability scoring layer",
  "NIST FIPS 203/204 compliant key encapsulation assessment",
];

export default function RiskMitigation() {
  const nav = useNavigate();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="risk"
      data-testid="risk-mitigation-section"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12 border-y border-slate-800 relative overflow-hidden"
    >
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none opacity-[0.015]"
        style={{ background: "radial-gradient(ellipse at 100% 50%, #D4AF37, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          {/* Left: Content - 7 cols */}
          <div className="lg:col-span-7">
            <ScrollReveal>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-4">
                Risk Framework
              </p>
              <h2
                data-testid="risk-heading"
                className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight mb-3"
              >
                The Sovereign Value Score
              </h2>
              <p className="text-sm text-[#D4AF37]/60 tracking-wide uppercase mb-8">
                Q-Day hardened digital asset appraisal
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8">
                JCAM's proprietary Sovereign Value Score (SVS) integrates on-chain
                provenance, regulatory risk exposure, quantum vulnerability metrics,
                and liquidity depth into a single, auditable metric. Designed for
                CFOs and Bank Officers who require defensible, board-ready asset
                appraisals — hardened against both current and post-quantum threat models.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="space-y-3 mb-10">
                {bulletPoints.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-px bg-[#D4AF37] mt-[10px] flex-shrink-0" />
                    <span className="text-sm text-slate-300 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-4">
                <button
                  data-testid="request-svs-report"
                  onClick={() => scrollTo("contact")}
                  className="border border-slate-700 text-slate-300 px-6 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                >
                  Request SVS Report
                </button>
                <button
                  data-testid="take-assessment-cta"
                  onClick={() => nav("/assessment")}
                  className="border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
                >
                  Take Q-Day Assessment
                </button>
              </div>
            </ScrollReveal>
          </div>

          {/* Right: Gauge + Metrics - 5 cols */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <ScrollReveal delay={0.2}>
              <SVSGauge />
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="grid grid-cols-2 gap-6 mt-12">
                {metrics.map((metric, i) => (
                  <div
                    key={metric.label}
                    data-testid={`metric-${i}`}
                    className="border-l border-slate-800 pl-5"
                  >
                    <span className="font-serif text-xl font-bold text-white block">{metric.value}</span>
                    <span className="text-[10px] tracking-[0.12em] uppercase text-slate-500 mt-1 block leading-relaxed">
                      {metric.label}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
